import React from "react";
import demoLogo from "../../../assets/svg/my brand/demo-logo.png";
import { BrandDetails } from "../types/brand.types";

interface BrandMarkProps {
  details: BrandDetails;
  /** Height of the lockup area in px; the logo scales to fill it. */
  size?: number;
  /** Fixed width in px; when omitted, width follows the artwork. */
  width?: number;
  /**
   * Alternate box for a roughly square upload, used alongside `width`/`size`
   * — the box for everything else. Which one applies is decided once the
   * uploaded logo's own aspect ratio is known, not by how it was cropped:
   * a manual crop is saved at whatever resolution was selected, so the
   * document showing it is what has to tell a wordmark lockup from an icon.
   * Left unset for a caller that doesn't need shape-aware sizing.
   */
  squareSize?: { width: number; height: number };
  className?: string;
}

/**
 * The specimen lockup shown until a logo is uploaded — the droplet and
 * wordmark the design pairs as "yourlogo", shipped as the artwork itself so
 * the preview shows the same mark the design does. Height-bound like a real
 * logo, so it stands in at the size the uploaded one will occupy.
 */
const PlaceholderLockup: React.FC<{ size: number; width?: number }> = ({
  size,
  width,
}) => (
  <img
    src={demoLogo}
    alt="yourlogo"
    style={{ height: size, width }}
    className={`object-left ${width ? "object-contain" : "w-auto object-contain"}`}
  />
);

/**
 * The logo as it appears on every generated document.
 *
 * An uploaded logo is shown on its own and given the whole lockup area, the
 * way the Naurist logo sits on the auth screens: a logo already carries its
 * own wordmark, so setting the brand name beside one printed the name twice.
 * Left-aligned rather than centred — the block below it (the brand name, in
 * the invoice) starts at the same left edge, so the two read as one lockup
 * instead of drifting apart when the logo is narrower than its box.
 */
const BrandMark: React.FC<BrandMarkProps> = ({
  details,
  size = 48,
  width,
  squareSize,
  className = "",
}) => {
  // A stored logo is fetched from the API's origin, and that fetch can fail —
  // the file moved, the media host is down. The specimen lockup is a better
  // answer than a broken-image icon on a document meant to be sent to a
  // client. Held as the src that failed rather than a flag, so picking a new
  // logo is a fresh attempt without an effect to reset it.
  const [failedSrc, setFailedSrc] = React.useState<string | null>(null);
  // Read off the loaded image itself — a manual crop carries no metadata
  // saying what shape it was aimed at, only the pixels it ended up with.
  const [isSquare, setIsSquare] = React.useState(false);
  const src = details.logo?.dataUrl ?? "";

  const useSquareBox = Boolean(squareSize && isSquare);
  const boxWidth = useSquareBox ? squareSize!.width : width;
  // The wide box's height follows the artwork — a manual crop can come out
  // at any aspect now, and holding it to exactly `size` was shrinking some
  // crops well below the width they were given. `size` still caps it,
  // though: a very tall/narrow crop shouldn't be free to blow out the row
  // it sits in. Square stays fixed both ways for now; only the wide lockup
  // goes auto-with-a-ceiling.
  const wideAutoHeight = Boolean(squareSize) && !useSquareBox;
  const boxHeight = useSquareBox ? squareSize!.height : size;

  return (
    <div
      className={`flex items-center justify-start ${className}`}
      style={{
        height: wideAutoHeight ? undefined : boxHeight,
        maxHeight: wideAutoHeight ? boxHeight : undefined,
      }}
    >
      {src && failedSrc !== src ? (
        // Height-bound with the width left to the artwork unless a box is
        // given, so a wide lockup stays a wide lockup instead of being
        // squared off into an icon. The ceiling is what keeps a long one
        // clear of the block beside it.
        <img
          src={src}
          alt=""
          style={{
            height: wideAutoHeight ? undefined : boxHeight,
            maxHeight: wideAutoHeight ? boxHeight : undefined,
            width: boxWidth,
            maxWidth: boxWidth ? undefined : size * 6,
          }}
          className={`object-left ${
            boxWidth ? "object-contain" : "w-auto object-contain"
          }`}
          onLoad={
            squareSize
              ? (event) => {
                  const image = event.currentTarget;
                  const ratio = image.naturalWidth / image.naturalHeight;
                  setIsSquare(ratio > 0.9 && ratio < 1.1);
                }
              : undefined
          }
          onError={() => setFailedSrc(src)}
        />
      ) : (
        <PlaceholderLockup size={size} width={width} />
      )}
    </div>
  );
};

export default BrandMark;
